import { useEffect, useState } from "react";
import { useDrop, DropTargetMonitor, useDragLayer, DragLayerMonitor } from "react-dnd";
import { useFormikContext } from "formik";
import { IPositioningFlight, IBookingFlight, IBookingCrew, IUser } from "@vmosolutions/oc-interfaces";
import { PosFlightChip } from "src/components/Pairings/PosFlightChip";
import { Theme, Box, alpha } from "@mui/material";
import { IBookingForm } from "./Forms/BookingForm";

interface IBookingFlightProps {
  bookingFlights?: IBookingFlight[];
}

// Immutable remove with spread operator
const remove = (items: any[], index: number): any[] => {
  return [...items.slice(0, index), ...items.slice(index + 1, items.length)];
};

export const BookingFlightDropTarget = (props: IBookingFlightProps): JSX.Element => {
  const bookingForm: IBookingForm = useFormikContext();

  const [pendingFlight, setPendingFlight] = useState<IPositioningFlight>(null);

  // Monitor item being dragged, return as valid if type matches accepted.
  const { isValidDraggable } = useDragLayer((monitor: DragLayerMonitor) => {
    const draggedItem: any = monitor.getItem();
    return {
      isValidDraggable: monitor.isDragging() && draggedItem?.type === "POSFLIGHT",
    };
  });

  // HANDLE FLIGHTS if dragged over eventgroups
  const [{ isOver }, drop] = useDrop({
    accept: ["POSFLIGHT"],
    drop(item: any, monitor: DropTargetMonitor) {
      let flight: IPositioningFlight = null;
      switch (item.type) {
        case "POSFLIGHT":
          flight = item.flight;
          break;
        default:
        // code block
      }
      const { _id, airlineIATA, flightNumber, origin, destination, outTime, inTime } = flight;
      const bookingFlight: IBookingFlight = {
        _id: _id,
        airlineIATA: airlineIATA,
        flightNumber: flightNumber,
        origin: origin,
        destination: destination,
        departure: outTime,
        arrival: inTime,
        changedFields: [],
      };

      const updatedBookingCrew = bookingForm?.values?.crew?.length > 0 ? [...bookingForm.values.crew] : [];
      const crewIds = updatedBookingCrew?.map((crew) => crew?.crewInfo?._id);
      // Loop through crew members listed on the drag object (if any)
      // and Add them to the booking if they don't already exist.
      let crewHasChanged = false;
      if (item?.flight?.crew?.length > 0) {
        (item.flight.crew as IUser[]).forEach((crew) => {
          // If this crew member doesn't exist in the booking, add them.
          if (!crewIds.includes(crew._id)) {
            const newCrewOnBooking: IBookingCrew = {
              crewInfo: {
                _id: crew._id,
                firstName: crew.firstName,
                lastName: crew.lastName,
              },
              employeeId: "",
            };
            // Push crew onto booking.
            crewHasChanged = true;
            updatedBookingCrew.push(newCrewOnBooking);
          }
        });
      }

      if (crewHasChanged) {
        // console.log("updatedBookingCrew", updatedBookingCrew);
        bookingForm.setFieldValue("crew", updatedBookingCrew);
        bookingForm.setFieldTouched("crew", true);
      }

      // console.log("bookingFlight", bookingFlight);
      const currentFlights = bookingForm.values.bookingFlights;
      const matchingflight = currentFlights.find((flight: IBookingFlight) => flight._id === bookingFlight._id);
      if (!matchingflight) {
        bookingForm.setFieldValue("bookingFlights", [...currentFlights, bookingFlight]);
        bookingForm.setFieldTouched("bookingFlights", true);
      }
    },

    hover(item: any, monitor: DropTargetMonitor) {
      let flight: IPositioningFlight = null;
      switch (item.type) {
        case "POSFLIGHT":
          flight = item.flight;
          break;
        default:
        // code block
      }
      const hasChanged = flight?._id !== pendingFlight?._id;
      if (hasChanged) {
        setPendingFlight(flight);
      }
    },

    canDrop(item: any, monitor: DropTargetMonitor) {
      return item.type === "POSFLIGHT" && item.flight;
    },

    collect(monitor: DropTargetMonitor) {
      return {
        isOver: monitor.isOver(),
      };
    },
  });

  useEffect(() => {
    if (!isOver) {
      setPendingFlight(null);
    }
  }, [isOver]);

  const meta = bookingForm.getFieldMeta("bookingFlights");

  // If users click on a flight chip, remove that one from the bookingflights.
  const removeFlight = (id: string): void => {
    const flights = meta.value as IBookingFlight[];
    const removeIndex = flights.findIndex((flight: IBookingFlight) => flight._id === id);
    if (removeIndex > -1) {
      const newList = remove(flights, removeIndex);
      bookingForm.setFieldValue("bookingFlights", newList);
      bookingForm.setFieldTouched("bookingFlights", true);
    }
  };

  const DropZoneStyles = (theme: Theme): any => ({
    event_dropzone: {
      display: "flex",
      cursor: "pointer",
      minHeight: "40px",
      justifyContent: "center",
      alignItems: "flex-start",
      flexDirection: "column",
      flexGrow: 1,
      backgroundColor: theme.palette.background.paper,
      borderRadius: "3px",
      backgroundImage: `linear-gradient(90deg, ${alpha(
        theme.custom.forms.focusedColor,
        0.25
      )} 100%, transparent 0%), linear-gradient(90deg, ${alpha(
        theme.custom.forms.focusedColor,
        0.25
      )} 100%, transparent 0%), linear-gradient(0deg, ${alpha(
        theme.custom.forms.focusedColor,
        0.25
      )} 100%, transparent 0%), linear-gradient(0deg, ${alpha(
        theme.custom.forms.focusedColor,
        0.25
      )} 100%, transparent 0%)`,
      backgroundRepeat: "repeat-x, repeat-x, repeat-y, repeat-y",
      backgroundSize: "8px 1px, 8px 1px, 1px 8px, 1px 8px",
      backgroundPosition: "0% 0%, 20% 100%, 0% 100%, 100% 0%",
      padding: "4px",
      "& .title": { fontSize: 18, fontWeight: "bold" },
      "& .body": {
        display: "flex",
        border: `2x solid ${theme.custom.forms.inputBorder}`,
        minHeight: "40px",
        alignItems: "center",
        color: theme.custom.forms.focusedColor,
        fontSize: 12,
        fontWeight: 300,
        textAlign: "center",
        padding: "4px 8px",
        opacity: 0.5,
        userSelect: "none",
      },
      "&.disabled": {
        opacity: 0.5,
        userSelect: "none",
        pointerEvents: "none",
      },
      "&.reject": {
        backgroundcolor: theme.custom.forms.errorColor,
      },
      "&.active": {
        backgroundcolor: theme.custom.forms.focusedColor,
      },
      "&.dirty": {
        backgroundcolor: theme.custom.forms.dirtyColor,
      },
      "&.over": {
        backgroundcolor: theme.custom.forms.dirtyColor,
      },
    },
  });

  return (
    <Box
      className={` booking_drop ${meta.touched && "dirty"} ${isValidDraggable && "active"} ${isOver && "over"}`}
      ref={drop}
      sx={(theme) => DropZoneStyles(theme).event_dropzone}
    >
      <div className="body">
        Flights: Drag/drop positioning flights into this zone from pairing eventgroup table. Click on flight chip to
        remove it from booking.
      </div>
      <div style={{ display: "flex", padding: "10px" }}>
        {bookingForm?.values?.bookingFlights?.length > 0
          ? bookingForm.values.bookingFlights.map((flight: IBookingFlight) => {
              const { _id, origin, destination, departure, arrival, airlineIATA, flightNumber } = flight;
              const posFlight: IPositioningFlight = {
                _id,
                origin,
                destination,
                outTime: departure,
                inTime: arrival,
                airlineIATA,
                flightNumber,
                bookings: [],
                isGround: false,
                createdBy: null,
                createdAt: null,
              };
              return (
                <div key={flight._id} className="booking_flight">
                  <PosFlightChip data={posFlight} onClickChip={removeFlight} />
                </div>
              );
            })
          : null}

        {!!pendingFlight?._id && (
          <div style={{ opacity: 0.5 }} key={pendingFlight._id}>
            <PosFlightChip data={pendingFlight} />
          </div>
        )}
      </div>
    </Box>
  );
};
